import React from "react";
import {
  Iconography,
  IconographySizes,
  Notification,
  NotificationVariants,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import {
  createRoleSagaAction,
  deleteRoleSagaAction,
  getUserRolePermissionsSagaAction,
  upsertRolePermissionsSagaAction,
  updateRoleSagaAction,
} from "../../../../redux/saga/user/saga-actions";
import RolesHeader from "./components/RolesHeader";
import RolesGrid from "./components/RolesGrid";
import RoleFormModal from "./components/RoleFormModal";
import DeleteRoleModal from "./components/DeleteRoleModal";
import RolePermissionsDetail from "./components/RolePermissionsDetail";
import { RoleCardData } from "./data/mockRoles";
import { ROLE_TOAST_MESSAGES } from "./data/roleToastMessages";
import type { RoleFormModalMode } from "./components/RoleFormModal";
import "./RolesAndPermissions.scss";

const ROLE_TOAST_AUTO_CLOSE_MS = 4500;

const ROLES_PAGE_BODY_ATTR = "data-roles-permissions-page";

interface RolesAndPermissionsProps {
  onDetailViewChange?: (isDetailView: boolean) => void;
  onRegisterAddNewRole?: (handler: () => void) => void;
  readOnly?: boolean;
  hideHeader?: boolean;
}

const RolesAndPermissions: React.FC<RolesAndPermissionsProps> = ({
  onDetailViewChange,
  onRegisterAddNewRole,
  readOnly = false,
  hideHeader = false,
}) => {
  const dispatch = useAppDispatch();
  const { userRolePermissions, status } = useAppSelector((state) => state.user);

  const [roleModalOpen, setRoleModalOpen] = React.useState(false);
  const [roleModalMode, setRoleModalMode] = React.useState<"add" | "edit">(
    "add"
  );
  const [editingRole, setEditingRole] = React.useState<RoleCardData | null>(
    null
  );
  const [roleToDelete, setRoleToDelete] = React.useState<RoleCardData | null>(
    null
  );
  const [toasts, setToasts] = React.useState<any[]>([]);
  const [selectedRoleForPermissions, setSelectedRoleForPermissions] =
    React.useState<RoleCardData | null>(null);

  const pushSuccessRoleToast = (message: string) => {
    setToasts((prev) => [
      ...prev,
      {
        id: Date.now(),
        title: "",
        message,
        variant: NotificationVariants.Success,
        icon: (
          <Iconography icon="circleCheck" size={IconographySizes.Medium} />
        ),
      },
    ]);
  };

  const pushDeleteRoleToast = (message: string) => {
    setToasts((prev) => [
      ...prev,
      {
        id: Date.now(),
        title: "",
        message,
        variant: NotificationVariants.Error,
        icon: (
          <Iconography icon="trashCan" size={IconographySizes.Medium} />
        ),
      },
    ]);
  };

  const handleRoleFormSuccess = (mode: RoleFormModalMode) => {
    const message =
      mode === "add"
        ? ROLE_TOAST_MESSAGES.added
        : ROLE_TOAST_MESSAGES.updated;
    pushSuccessRoleToast(message);
  };

  React.useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    document.body.setAttribute(ROLES_PAGE_BODY_ATTR, "true");
    return () => {
      document.body.removeAttribute(ROLES_PAGE_BODY_ATTR);
    };
  }, []);

  React.useEffect(() => {
    onDetailViewChange?.(Boolean(selectedRoleForPermissions));
  }, [selectedRoleForPermissions, onDetailViewChange]);

  React.useEffect(() => {
    dispatch(getUserRolePermissionsSagaAction());
  }, [dispatch]);

  const roleCards = React.useMemo<RoleCardData[]>(() => {
    const data = userRolePermissions as unknown as
      | Record<string, unknown>
      | Array<Record<string, unknown>>
      | undefined;

    const formatDate = (value: unknown) => {
      if (!value) return "-";
      const date = new Date(String(value));
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    const getRoleBadge = (type: unknown) => {
      const roleType = String(type ?? "").trim();
      return roleType || "Role";
    };

    if (Array.isArray(data)) {
      return data
        .map((item) => {
          const roleName = String(
            item?.role ?? item?.name ?? item?.role_name ?? item?.roleName ?? ""
          ).trim();
          if (!roleName) return null;

          return {
            id: String(item?.ID ?? roleName.toLowerCase().replace(/[^a-z0-9]+/g, "-")),
            apiId: Number(item?.ID ?? 0),
            name: roleName,
            description:
              String(item?.description ?? "").trim() ||
              "No description provided.",
            status: Number(item?.status ?? 1) === 0 ? 0 : 1,
            usersCount: Number(item?.user_count ?? 0),
            createdOn: formatDate(item?.created_at),
            roleType: getRoleBadge(item?.type),
            cardIcon:
              String(item?.type ?? "").toLowerCase().includes("partner")
                ? ("globe" as const)
                : ("lock" as const),
            canDelete:
              String(item?.type ?? "")
                .trim()
                .toLowerCase() !== "super admin",
          };
        })
        .filter((role): role is RoleCardData => Boolean(role));
    }
    if (data && typeof data === "object") {
      return Object.keys(data).map((roleName) => ({
        id: roleName.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        apiId: 0,
        name: roleName,
        description: "No description provided.",
        status: 1,
        usersCount: 0,
        createdOn: "-",
        roleType: "Role",
        cardIcon: roleName.toLowerCase().includes("partner") ? "globe" : "lock",
        canDelete: roleName.toLowerCase() !== "super admin",
      }));
    }
    return [];
  }, [userRolePermissions]);

  const onAddNewRole = React.useCallback(() => {
    setEditingRole(null);
    setRoleModalMode("add");
    setRoleModalOpen(true);
  }, []);

  React.useEffect(() => {
    onRegisterAddNewRole?.(onAddNewRole);
  }, [onRegisterAddNewRole, onAddNewRole]);

  const handleEditRole = (role: RoleCardData) => {
    setEditingRole(role);
    setRoleModalMode("edit");
    setRoleModalOpen(true);
  };

  const handleOpenRolePermissions = (role: RoleCardData) => {
    setSelectedRoleForPermissions(role);
  };

  const handleBackFromRolePermissions = () => {
    setSelectedRoleForPermissions(null);
  };

  const onCloseRoleModal = () => {
    setRoleModalOpen(false);
    setEditingRole(null);
  };

  const handleDeleteRole = (role: RoleCardData) => {
    setRoleToDelete(role);
  };

  const onCloseDeleteModal = () => {
    setRoleToDelete(null);
  };

  const handleConfirmDeleteRole = () => {
    if (!roleToDelete || roleToDelete.apiId <= 0) {
      pushDeleteRoleToast("Unable to delete role. Missing role ID.");
      setRoleToDelete(null);
      return;
    }

    dispatch(
      deleteRoleSagaAction({
        ID: roleToDelete.apiId,
        onSuccess: (message) => {
          pushDeleteRoleToast(message || ROLE_TOAST_MESSAGES.deleted);
          setRoleToDelete(null);
        },
        onError: (message) => {
          pushDeleteRoleToast(message);
        },
      })
    );
  };

  const handleSaveRolePermissions = (payload: {
    role_id: number;
    permissions: Array<{ permission_id: number; action: string }>;
  }) => {
    dispatch(
      upsertRolePermissionsSagaAction({
        ...payload,
        onSuccess: (message) => {
          pushSuccessRoleToast(message || "Permissions updated successfully.");
        },
        onError: (message) => {
          pushDeleteRoleToast(message);
        },
      })
    );
  };

  const handleCreateRole = (payload: {
    name: string;
    type: string;
    description: string;
    clone_from_role_id?: number;
  }) => {
    dispatch(
      createRoleSagaAction({
        ...payload,
        onSuccess: (message) => {
          pushSuccessRoleToast(message || ROLE_TOAST_MESSAGES.added);
          onCloseRoleModal();
        },
        onError: (message) => {
          pushDeleteRoleToast(message);
        },
      })
    );
  };

  const handleUpdateRole = (payload: {
    ID: number;
    name: string;
    type: string;
    description: string;
    status: 0 | 1;
  }) => {
    dispatch(
      updateRoleSagaAction({
        ...payload,
        onSuccess: (message) => {
          pushSuccessRoleToast(message || ROLE_TOAST_MESSAGES.updated);
          onCloseRoleModal();
        },
        onError: (message) => {
          pushDeleteRoleToast(message);
        },
      })
    );
  };

  return (
    <div className="roles-permissions-container">
      <div className="roles-permissions-notifications-host">
        <Notification
          toasts={toasts}
          setToasts={setToasts}
          autoClose={ROLE_TOAST_AUTO_CLOSE_MS}
          isAutoClose={true}
        />
      </div>
      {!selectedRoleForPermissions && !hideHeader && (
        <RolesHeader onAddNewRole={onAddNewRole} readOnly={readOnly} />
      )}
      <div className="roles-permissions-content">
        {selectedRoleForPermissions ? (
          <RolePermissionsDetail
            role={selectedRoleForPermissions}
            onBack={handleBackFromRolePermissions}
            onSave={handleSaveRolePermissions}
            readOnly={readOnly}
          />
        ) : status.userRolePermissionsLoading ? (
          <div className="rp-roles-loading">Loading roles...</div>
        ) : roleCards.length === 0 ? (
          <div className="rp-roles-loading">No roles found.</div>
        ) : (
          <RolesGrid
            roles={roleCards}
            onEditRole={handleEditRole}
            onDeleteRole={handleDeleteRole}
            onOpenRole={handleOpenRolePermissions}
            readOnly={readOnly}
          />
        )}
      </div>

      <RoleFormModal
        open={roleModalOpen}
        mode={roleModalMode}
        onClose={onCloseRoleModal}
        initialRole={editingRole}
        existingRoles={roleCards}
        onSubmitAdd={handleCreateRole}
        onSubmitEdit={handleUpdateRole}
        onSuccess={handleRoleFormSuccess}
      />

      <DeleteRoleModal
        open={roleToDelete !== null}
        role={roleToDelete}
        onClose={onCloseDeleteModal}
        onConfirm={handleConfirmDeleteRole}
      />
    </div>
  );
};

export default RolesAndPermissions;
